import { Observable, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Component, DestroyRef, HostListener, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AnalyticsService, AuthService } from '@gs-2.0/core';
import { LoadingService } from '@gs-2.0/shared';

import { environment } from '../environments/environment';

declare global {
  interface Window {
    clarity: ((...args: any[]) => void) & { q?: any[] };
  }
}
import Urls from 'libs/portal/src/lib/_core/constants/urls';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'gs-2.0';
  isAuthenticated$!: Observable<boolean>;

  /** True on routes that declare `data: { hideShell: true }` (e.g. register). */
  hideShell = signal(window.location.pathname.includes('/register'));

  private _showingNavigationOverlay = false;

  constructor(
    private authService: AuthService,
    public loadingService: LoadingService,
    private router: Router,
    private destroyRef: DestroyRef,
    private analyticsService: AnalyticsService,
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Walk up the DOM to find the first interactive element
    const button = target.closest('button');
    const anchor = target.closest('a');

    // Prefer the closest button; only track anchor if there is no button ancestor
    if (button) {
      // Skip buttons that have dedicated analytics tracking
      if (button.hasAttribute('data-analytics-skip')) return;
      const label =
        button.getAttribute('aria-label') ||
        button.getAttribute('data-analytics-label') ||
        button.textContent?.trim() ||
        'unknown';
      this.analyticsService.trackButtonClick(label);
    } else if (anchor) {
      const href = anchor.getAttribute('href') || '';
      const label =
        anchor.getAttribute('aria-label') ||
        anchor.getAttribute('data-analytics-label') ||
        anchor.textContent?.trim() ||
        href;
      // this.analyticsService.trackLinkClick(href, label);
    }
  }

  ngOnInit(): void {
    this.isAuthenticated$ = this.authService.isAuthenticated$;

    // Initialise GA after settings are available (settings are loaded by APP_INITIALIZER)
    this.analyticsService.initialize();

    // Initialise Microsoft Clarity if a tracking ID is configured for this environment
    this.initClarity();

    // Hide the app shell on NavigationEnd; also manage the /fund/donate loading overlay.
    // Page title and analytics page_view are handled by AnalyticsService internally.
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          // Use router.routerState.snapshot (fully committed) rather than
          // ActivatedRoute.firstChild, which can lag for lazy-loaded modules.
          const snapshot = this.getLeafSnapshot(this.router.routerState.snapshot.root);
          this.hideShell.set(snapshot.data['hideShell'] === true);
        }

        if (event instanceof NavigationStart && event.url.startsWith(`/${Urls.FUND}`)) {
          this._showingNavigationOverlay = true;
          this.loadingService.show();
        } else if (
          this._showingNavigationOverlay &&
          (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError)
        ) {
          this._showingNavigationOverlay = false;
          this.loadingService.hide();
        }
      });

    // When an invite link is opened on this device while a user is logged in,
    // the register page clears tokens and broadcasts via localStorage.
    // Other open tabs pick it up here and reload to home, resetting their auth state.
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter(e => e.key === 'auth:logout-broadcast'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        window.location.href = '/home';
      });

    // Note: General dropdowns (currencies and languages) are initialized via APP_INITIALIZER in app.module.ts
    // No need to call initializeDropdowns() here to avoid duplicate API calls
  }

  private initClarity(): void {
    const trackingId = environment.clarityTrackingId?.trim();
    if (!trackingId) return;

    window.clarity = window.clarity || function (...args: any[]) {
      (window.clarity.q = window.clarity.q || []).push(args);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${trackingId}`;
    document.head.appendChild(script);
  }

  /** Traverses the committed route snapshot tree to find the deepest child. */
  private getLeafSnapshot(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  logout(): void {
    this.authService.logout();
  }

  onRefresh(): void {
    window.location.reload();
  }

  onContactSupport(): void {
    window.open(environment.fund.contact, '_blank');
  }
}
